import { PlanOnboardingStep } from "@/services/SavingsPlanOnboarding.types";
import { ContractOnboardingStrings } from "@/screen/Map/contractOnboarding.strings";
import { resolveContractOnboardingStepNavigation } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { isReentrantContractStepKey, isSuitabilityContractStepKey } from "@/screen/Map/contractReentrantSteps";
import { MinorToAdultDataStepKey } from "@/screen/Map/minorToAdultFlow";

export type ContractMapStepActionKind = "navigate" | "advanceStep" | "none";

export interface ContractMapStepCardAction
{
    /** Botón derecho inactivo. */
    isActionDisabled: boolean;
    /** Tarjeta atenuada cuando el paso aún no es navegable. */
    isDimmed: boolean;
    isCurrent: boolean;
    actionLabel: string;
    actionKind: ContractMapStepActionKind;
}

// Un paso ya superado sigue `enabled` (significa "los anteriores están completos"), así que la
// condición del paso vivo es isCurrent, igual que en useContractStepGuard.
export function isStepActionable(step: PlanOnboardingStep): boolean
{
    return step.enabled && (step.isCurrent || isReentrantContractStepKey(step.key));
}

/**
 * Deriva el estado visual/interactivo de la tarjeta a partir del paso del backend.
 * El paso actual nunca se atenúa: es donde está el usuario y donde se explica qué falta.
 *
 * - Sección 2: navegar a destino FE si el paso es accionable y hay ruta.
 * - 3.2 / 3.3: `advanceStep` (botón Revisar) si el paso es accionable.
 * - Resto informativo: sin acción.
 */
export function resolveContractMapStepCardAction(step: PlanOnboardingStep, savingsPlanId: number): ContractMapStepCardAction
{
    if(step.key === "3.2" || step.key === "3.3")
    {
        return{
            isActionDisabled: !isStepActionable(step),
            isDimmed: !step.enabled && !step.isCurrent,
            isCurrent: step.isCurrent,
            actionLabel: ContractOnboardingStrings.review,
            actionKind: "advanceStep",
        };
    }

    const navigation = resolveContractOnboardingStepNavigation(step.key, savingsPlanId);
    const canNavigate = navigation.type === "push";

    let actionLabel = ContractOnboardingStrings.go;
    if(step.key === "1.1")
    {
        actionLabel = ContractOnboardingStrings.chooseSubscription;
    }
    else if(step.key === "2.1")
    {
        actionLabel = step.isCurrent ? ContractOnboardingStrings.go : ContractOnboardingStrings.addOrModify;
    }
    else if(step.key === MinorToAdultDataStepKey)
    {
        actionLabel = ContractOnboardingStrings.completeMinorToAdultData;
    }
    else if(step.key === "2.2" || step.key === "2.4.2")
    {
        actionLabel = ContractOnboardingStrings.selectInterveners;
    }
    else if(isSuitabilityContractStepKey(step.key))
    {
        actionLabel = step.isCurrent ? ContractOnboardingStrings.go : ContractOnboardingStrings.suitabilityUpdateTest;
    }

    if(!canNavigate)
    {
        return{
            isActionDisabled: true,
            isDimmed: !step.enabled && !step.isCurrent,
            isCurrent: step.isCurrent,
            actionLabel: actionLabel,
            actionKind: "none",
        };
    }

    return{
        isActionDisabled: !isStepActionable(step),
        isDimmed: !step.enabled && !step.isCurrent,
        isCurrent: step.isCurrent,
        actionLabel: actionLabel,
        actionKind: "navigate",
    };
}